import { useContext, useEffect, useRef, useState } from "react";
import useTranslator from "../../hooks/useTranslator";
import TenantContext from "../../context/tenant/TenantContext";
import { useNavigate } from "react-router-dom";
import { toast } from "../../helper/common";

const DocumentScanning = ({
  user,
  formHandler,
  updateUserProfile,
  isGuarantor,
  runDetectKycDocs,
  usedFor,
  setShowDocScanningStep,
  setShowKycPopup,
  setAddedFiles,
}) => {
  const iconCirclesRef = useRef([]);
  const intervalRef = useRef(null); // 🔁 to store the interval ID
  const navigate = useNavigate();
  const { _t } = useTranslator();
  const [docAnalysisLoading, setDocAnalysisLoading] = useState(true);
  const { checkDocumentAnalysisStatus } = useContext(TenantContext);
  const [kycVerificationStatus, setKycVerificationStatus] =
    useState("PROCESSING");

  const cycleHighlight = () => {
    const iconCircles = iconCirclesRef.current;
    let currentIndex = 0;

    intervalRef.current = setInterval(() => {
      // Remove 'active' from all (first 3 only)
      iconCircles.slice(0, 3).forEach((circle) => {
        circle?.classList.remove("active");
      });

      // Add 'active' to current index (within first 3 only)
      if (iconCircles[currentIndex]) {
        iconCircles[currentIndex].classList.add("active");
      }

      currentIndex = (currentIndex + 1) % 3; // 🚨 only loop through 0,1,2
    }, 800);
  };

  const checkDocumentAnalysis = async () => {
    const tenantId = user?._id;
    if (!tenantId) return;

    const interval = setInterval(async () => {
      try {
        const res = await checkDocumentAnalysisStatus(tenantId, "kyc");

        if (res?.success_res?.manual_kyc_doc_status === "VERIFIED") {
          clearInterval(interval);
          setDocAnalysisLoading(false);
          setKycVerificationStatus("VERIFIED");
          clearInterval(intervalRef.current); // ❌ Stop animation
          setTimeout(() => {
            handleSubmit();
          }, 3000);
          // Remove active from first 3
          iconCirclesRef.current
            .slice(0, 3)
            .forEach((circle) => circle?.classList.remove("active"));

          // ✅ Permanently highlight 4th item (index 3)
          const finalCircle = iconCirclesRef.current[3];
          if (finalCircle) {
            finalCircle.classList.add("active");
          }

          return;
        }

        if (
          res?.success_res?.manual_kyc_doc_status === "UNVERIFIED" ||
          res?.success_res?.status === "failed"
        ) {
          clearInterval(interval);
          setDocAnalysisLoading(false);
          setKycVerificationStatus("UNVERIFIED");

          clearInterval(intervalRef.current); // ❌ Stop animation

          // Remove active from first 3
          iconCirclesRef.current
            .slice(0, 3)
            .forEach((circle) => circle?.classList.remove("active"));

          // ✅ Permanently highlight 4th item (index 3)
          const finalCircle = iconCirclesRef.current[3];
          if (finalCircle) {
            finalCircle.classList.add("active-failed");
          }

          toast.error(
            _t(
              "documentAnalysisFailedPleaseReUpload",
              "Text used in onboarding flow",
            ),
          );
          return;
        }

        if (
          res?.success_res?.manual_kyc_doc_status === "PROCESSING" ||
          res?.success_res?.status === "processing"
        ) {
          setDocAnalysisLoading(true);
          return;
        }

        console.warn("Unexpected status or missing data", res);
        clearInterval(interval);
        setKycVerificationStatus("UNVERIFIED");
      } catch (error) {
        console.error("Error checking document analysis:", error);
        clearInterval(interval);
        setDocAnalysisLoading(false);
        setKycVerificationStatus("UNVERIFIED");
      }
    }, 5000);
  };

  useEffect(() => {
    if (runDetectKycDocs) {
      checkDocumentAnalysis();
    }
    const cleanupInterval = cycleHighlight();
    return cleanupInterval;
  }, [runDetectKycDocs]);

  const handleSubmit = async () => {
    const role = localStorage.getItem("usertype");
    if (usedFor === "popup") {
      setShowDocScanningStep(false);
      setShowKycPopup(false);
    } else {
      const nextStep = user?.skip_steps?.includes("bank_onboarding") ? 7 : 6;
      await updateUserProfile({ step: nextStep, role });
    }
  };
  return (
    <>
      <div
        className={`popup-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 wrapper-modal modal-fixed-screen ${
          isGuarantor ? "guarantorBg" : ""
        }`}
        onClick={isGuarantor ? () => navigate("/home") : null}
      >
        <div
          className="popup-content2 popup-content bg-white required-doc-popup identity_modal"
          onClick={isGuarantor ? (e) => e.stopPropagation() : null}
        >
          <div className="row relativeposition">
            <div className="col-12 d-flex gap-4 align-items-center justify-content-center heading-section">
              <div className="authenticateid-header text-center">
                <h4>
                  {kycVerificationStatus === "PROCESSING"
                    ? _t(
                        "identityDocumentUploading",
                        "Text used in while scanning identity document at onboarding process",
                      )
                    : kycVerificationStatus === "UNVERIFIED"
                      ? _t("kycFailed")
                      : _t("keyPassed")}
                </h4>
                <p>
                  {kycVerificationStatus === "PROCESSING"
                    ? _t(
                        "yourIdentityDocumentHasBeenUploading",
                        "Text used in while scanning identity document at onboarding process",
                      )
                    : kycVerificationStatus === "UNVERIFIED"
                      ? _t("yourKycHasBeenFailed")
                      : _t("yourKycHasBeenPassed")}
                </p>
              </div>
            </div>
          </div>
          <div className="docomentsScann custom-my-5">
            <div className="row justify-content-center">
              <div className="col-12 d-flex flex-wrap justify-content-center">
                {kycVerificationStatus === "PROCESSING" ? (
                  <div className="icon-container">
                    <div
                      className="icon-circle"
                      ref={(el) => (iconCirclesRef.current[0] = el)}
                    >
                      <svg
                        width="80"
                        height="80"
                        viewBox="0 0 48 48"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect
                          x="8"
                          y="14"
                          width="32"
                          height="20"
                          rx="3"
                          stroke="#A78BFA"
                          strokeWidth="2"
                          strokeLinejoin="round"
                        />
                        <circle
                          cx="16"
                          cy="21"
                          r="3"
                          stroke="#A78BFA"
                          strokeWidth="2"
                        />
                        <path
                          d="M20 28C20 25.7909 18.2091 24 16 24C13.7909 24 12 25.7909 12 28"
                          stroke="#A78BFA"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <path
                          d="M25 19H35"
                          stroke="#A78BFA"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <path
                          d="M26 12V36"
                          stroke="#A78BFA"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <path
                          d="M25 24H35"
                          stroke="#A78BFA"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <path
                          d="M25 29H31"
                          stroke="#A78BFA"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                    <div
                      className="icon-circle"
                      ref={(el) => (iconCirclesRef.current[1] = el)}
                    >
                      <svg
                        width="80"
                        height="80"
                        viewBox="0 0 48 48"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M13 21V17C13 15.3431 14.3431 14 16 14H20"
                          stroke="#A78BFA"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M35 21V17C35 15.3431 33.6569 14 32 14H28"
                          stroke="#A78BFA"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M13 27V31C13 32.6569 14.3431 34 16 34H20"
                          stroke="#A78BFA"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M35 27V31C35 32.6569 33.6569 34 32 34H28"
                          stroke="#A78BFA"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <circle
                          cx="24"
                          cy="20"
                          r="3"
                          stroke="#A78BFA"
                          strokeWidth="2"
                        />
                        <path
                          d="M19 30 Q 24 23, 29 30"
                          stroke="#A78BFA"
                          strokeWidth="2"
                          strokeLinecap="round"
                          fill="none"
                        />
                      </svg>
                    </div>
                    <div
                      className="icon-circle"
                      ref={(el) => (iconCirclesRef.current[2] = el)}
                    >
                      <svg
                        width="80"
                        height="80"
                        viewBox="0 0 48 48"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle
                          cx="23"
                          cy="21"
                          r="9"
                          stroke="#A78BFA"
                          strokeWidth="2"
                        />
                        <path
                          d="M29.5 27.5L35 33"
                          stroke="#A78BFA"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <path
                          d="M18 19H28"
                          stroke="#A78BFA"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <path
                          d="M18 23H24"
                          stroke="#A78BFA"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                  </div>
                ) : kycVerificationStatus === "UNVERIFIED" ? (
                  <div className="animate-slow-bounce">
                    <div className="circle-container">
                      {/* 'X' Cross */}
                      <div className="x-wrapper">
                        {/* First bar of 'X' */}
                        <div className="x-bar rotate"></div>
                        {/* Second bar of 'X' */}
                        <div className="x-bar reverse-rotate"></div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="kyc-success-container">
                    <div className="kyc-success-perspective">
                      <div className="kyc-success-animation-box">
                        <div className="kyc-success-card">
                          <div className="kyc-success-card-shimmer"></div>
                          <div className="kyc-success-card-left">
                            <div className="kyc-success-card-avatar">
                              <svg
                                className="kyc-success-card-avatar-icon"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                />
                              </svg>
                            </div>
                            <div className="kyc-success-card-line"></div>
                          </div>
                          <div className="kyc-success-card-right">
                            <div className="kyc-success-card-lines">
                              <div className="kyc-success-card-line-long"></div>
                              <div className="kyc-success-card-line-medium"></div>
                            </div>
                            <div className="kyc-success-card-lines-small">
                              <div className="kyc-success-card-line-small"></div>
                              <div className="kyc-success-card-line-xsmall"></div>
                              <div className="kyc-success-card-line-small-medium"></div>
                            </div>
                          </div>
                        </div>
                        <div className="kyc-success-tick-container">
                          <div className="kyc-success-tick-circle">
                            <svg
                              className="kyc-success-tick-icon"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={3}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          {kycVerificationStatus !== "PROCESSING" && (
            <div
              className={`d-flex align-items-center justify-content-center go_back_btn_wrapper gap-3 ${
                kycVerificationStatus !== "VERIFIED"
                  ? "go_back_btn_wrapper_un"
                  : ""
              }`}
            >
              {kycVerificationStatus === "UNVERIFIED" && (
                <button
                  onClick={() => {
                    setAddedFiles([]);
                    if (usedFor === "popup") {
                      setShowDocScanningStep(false);
                    } else {
                      formHandler.prev();
                    }
                  }}
                  type="button"
                  className="btnNext btnnextpre uploadbtn go_back_btn full_radius"
                >
                  {_t("goBackAndReupload")}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default DocumentScanning;
